import { Box, Divider, Typography } from '@mui/material';
import Mui from 'materials';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { AxiosResponse } from 'axios';
import ActionButtons from 'components/action-buttons';
import LoadingSpinner from 'components/loading-spinner';
import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { usePost } from 'hooks/use-post';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { MarketId } from 'types/market-id.enum';
import { updatePreferencesSettings } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/api/update-ofd-settings';
import { useInputSettingsState } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/context/input-settings-context';
import { OFD2Params } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/ofd-2-params.type';
import { UpdatePreferencesInput } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/ofd-settings-input.type';
import { HeaderWrapper } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/utils/styled-components';
import { useGoogleTagManager } from 'views/order-feed/features/my-supply-plan/hooks/use-google-tag-manager';
import NumberField from 'components/number-field';

type Props = {};

const PAGE_NAME_ID = 'orderFeed.ofdMySupplyPlan.inputSettings.preferencesTab';

const PreferencesTab: React.FC<Props> = () => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const params = useParams<OFD2Params>();
  const snackbarDispatch = useSnackbarDispatch();
  const { createInputSettingsEvent } = useGoogleTagManager();
  const {
    account: { company },
  } = useAccountState();
  const {
    getOfdSettingsQuery: { data: ofdSettings, isFetching },
  } = useInputSettingsState();

  const isChile = useMemo(() => {
    return company?.marketId === MarketId.Chile;
  }, [company]);

  const [edit, setEdit] = useState<boolean>(false);

  const updatePreferences = usePost<AxiosResponse, UpdatePreferencesInput>(
    (request) => updatePreferencesSettings(request, params.globalSiteId),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      queryClient.invalidateQueries({
        queryKey: ['getOfdSettings']
      });
      queryClient.invalidateQueries({
        queryKey: ['getTimeline']
      });
      queryClient.invalidateQueries({
        queryKey: ['getRecommendationAssortment']
      });

      setEdit(false);
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: {
          msg: intl.formatMessage({ id: 'snackbar.errors.default' }),
          error: error,
        },
      });
    }
  );

  const handleCancel = () => setEdit(false);

  const handleEdit = (edited: boolean) => setEdit(edited);

  const defaultValues: UpdatePreferencesInput = useMemo(() => {
    return {
      minimumRecommendationQuantity: ofdSettings?.minimumRecommendationQuantity ?? 0,
      orderFrequency: isChile ? (ofdSettings?.orderFrequency ?? null) : null,
    };
  }, [ofdSettings, isChile]);

  const { control, handleSubmit, reset } = useForm<UpdatePreferencesInput>({
    defaultValues: defaultValues,
    shouldUnregister: true,
  });

  const handleUpdatePreferences = (data: UpdatePreferencesInput) => {
    data.orderFrequency = isChile ? Number(data.orderFrequency) : null; // Check with Braulio why this Number() cast exists. This was not present before, but causes Norway sites to fail the update call because it casts null to 0

    updatePreferences.mutate(data, {
      onSuccess: () => {
        if (ofdSettings) {
          createInputSettingsEvent('save_preferences', {
            oldValue1: String(ofdSettings.minimumRecommendationQuantity),
            newValue1: String(data.minimumRecommendationQuantity),
            section: 'inputSettings',
          });
        }
      },
    });
  };

  useEffect(() => {
    if (!edit) reset(defaultValues);
  }, [edit, ofdSettings]);

  return (
    <>
      <Mui.Paper sx={{ minHeight: 600, p: 6 }}>
        <form>
          <HeaderWrapper>
            <Box>
              <Typography variant="h3" fontWeight={700} mb={2}>
                <FormattedMessage id={`${PAGE_NAME_ID}.label`} />
              </Typography>
              <Typography variant="body1" mb={4}>
                <FormattedMessage id={`${PAGE_NAME_ID}.headerText`} />
              </Typography>
            </Box>
            <Box>
              <ActionButtons
                isEditing={edit}
                onEdit={handleEdit}
                onCancel={handleCancel}
                isLoading={updatePreferences.isPending}
                onSave={handleSubmit(handleUpdatePreferences)}
              />
            </Box>
          </HeaderWrapper>
          <Divider />
          <Typography variant="body1" fontWeight={'bold'} mt={5}>
            <FormattedMessage id={`${PAGE_NAME_ID}.minimumQuantityTitle`} />
          </Typography>
          <Typography variant="body1" mb={5}>
            <FormattedMessage id={`${PAGE_NAME_ID}.minimumQuantityText`} />
          </Typography>
          <Box display={'flex'} flexDirection={'column'} gap={4}>
            <Box>
              {isFetching ? (
                <LoadingSpinner />
              ) : (
                <NumberField
                  dark
                  fullWidth
                  disabled={!edit}
                  endAdornment="kg"
                  controller={{
                    control,
                    name: 'minimumRecommendationQuantity',
                    rules: {
                      validate: {
                        multipleOf: (value) => {
                          const multiple = isChile ? 1250 : 500;
                          return Number(value) % multiple === 0
                            ? true
                            : intl.formatMessage({ id: `${PAGE_NAME_ID}.quantityErrorMessage` }, { value: multiple });
                        },
                        minimumValue: (value) => {
                          const min = isChile ? 1250 : 500;
                          return value >= min
                            ? true
                            : intl.formatMessage(
                                { id: `${PAGE_NAME_ID}.minimumValueErrorMessage` },
                                { label: 'Quantity', minValue: min }
                              );
                        },
                      },
                    },
                  }}
                  label={intl.formatMessage({ id: `${PAGE_NAME_ID}.minimumQuantity` })}
                />
              )}
            </Box>
            <Divider />
            {isChile && (
              <Box>
                <Typography variant="body1" fontWeight={'bold'} mt={5}>
                  <FormattedMessage id={`${PAGE_NAME_ID}.minimumOrderFrequency`} />
                </Typography>
                <Typography variant="body1" mb={5}>
                  <FormattedMessage id={`${PAGE_NAME_ID}.minimumOrderFrequencyText`} />
                </Typography>
                {isFetching ? (
                  <LoadingSpinner />
                ) : (
                  <NumberField
                    dark
                    fullWidth
                    disabled={!edit}
                    endAdornment="days"
                    controller={{
                      control,
                      name: 'orderFrequency',
                      rules: {
                        required: intl.formatMessage({ id: 'form.rules.required' }),
                        validate: {
                          between1And30: (value) =>
                            value >= 1 && value <= 30 ? true : intl.formatMessage({ id: `${PAGE_NAME_ID}.rangeValueErrorMessage` }),
                        },
                      },
                    }}
                    label={intl.formatMessage({ id: `${PAGE_NAME_ID}.orderFrequency` })}
                  />
                )}
              </Box>
            )}
          </Box>
        </form>
      </Mui.Paper>
    </>
  );
};

export default PreferencesTab;
